/**
 * Voice Agent Subscription - TypeScript Types
 *
 * All voice billing types for pricing-svc /api/voice/* endpoints.
 * Mirrors the Pydantic schemas in pricing-svc/app/schemas/voice_schema.py.
 */

// ── Enums ──────────────────────────────────────────────────────────────────

export type VoiceTier = 'voice_launch' | 'voice_scale' | 'voice_enterprise';
export type VoiceBillingCycle = 'monthly' | 'annual';
export type VoiceSubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED';
export type VoiceCallType = 'webrtc' | 'pstn';
export type VoiceVertical =
  | 'home_services'
  | 'insurance'
  | 'medicare'
  | 'healthcare'
  | 'real_estate'
  | 'financial'
  | 'other';

/**
 * Success-fee pricing band for the 'other' vertical, chosen during onboarding
 * (StepActivate) based on what a confirmed appointment is worth to the tenant.
 * Not yet persisted server-side — see docs/BACKEND_VOICE_OUTCOME_BAND.md.
 */
export type VoiceOutcomeBand = 'standard' | 'elevated' | 'high';

export type VoiceSuccessEventType =
  | 'appointment_confirmed'
  | 'appointment_rescheduled'
  | 'appointment_cancelled'
  | 'callback_scheduled'
  | 'transfer_completed'
  | 'sms_delivered'
  | 'email_delivered'
  | 'url_push_delivered'
  | 'crm_write_confirmed'
  | 'data_lookup'
  | 'signal_emitted';

// ── Subscription ───────────────────────────────────────────────────────────

export interface VoiceSubscription {
  id: number;
  tenant_id: number;
  tier: VoiceTier | null;
  status: VoiceSubscriptionStatus | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_billing_period_start: string | null;
  current_billing_period_end: string | null;
  next_billing_date: string | null;
  billing_cycle?: VoiceBillingCycle;
  /** Monthly price in cents for the current tier. */
  platform_fee_cents?: number;
  /** Monthly-equivalent price in cents when billed annually. */
  annual_fee_cents?: number;
  subscription_started_at: string | null;
  trial_sessions_limit: number;
  trial_sessions_used: number;
  trial_sessions_remaining: number;
  trial_active: boolean;
  outcomes_track_enabled: boolean;
  vertical: VoiceVertical | null;
  /** Only meaningful when vertical === 'other'. See docs/BACKEND_VOICE_OUTCOME_BAND.md. */
  outcome_band?: VoiceOutcomeBand | null;
  monthly_included_minutes: number;
  minutes_rolled_over: number;
  concurrent_session_limit: number;
  phone_numbers_included: number;
  max_active_campaigns: number | null;
  max_agent_configs: number | null;
  memory_tier: string;
  warm_transfer_available: boolean;
  url_push_available: boolean;
  model_routing: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface VoiceSubscriptionResponse {
  success: boolean;
  message?: string;
  subscription?: VoiceSubscription;
  created?: boolean;
}

// ── Entitlements ──────────────────────────────────────────────────────────

export interface VoiceEntitlements {
  success: boolean;
  allowed: boolean;
  reason?: string;
  tier?: VoiceTier;
  status?: VoiceSubscriptionStatus;
  is_trial?: boolean;
  trial_sessions_remaining?: number;
  concurrent_limit?: number;
  monthly_included_minutes?: number;
  minutes_rolled_over?: number;
  phone_numbers_included?: number;
  max_active_campaigns?: number | null;
  max_agent_configs?: number | null;
  memory_tier?: string;
  warm_transfer_available?: boolean;
  url_push_available?: boolean;
  model_routing?: string;
  outcomes_track_enabled?: boolean;
  vertical?: VoiceVertical | null;
}

// ── Success Fees ───────────────────────────────────────────────────────────

export interface VoiceSuccessFeeSummaryLineItem {
  event_type: VoiceSuccessEventType;
  count: number;
  rate: number;
  total_usd: number;
}

export interface VoiceSuccessFeeSummary {
  success: boolean;
  tenant_id: number;
  billing_period: string;
  line_items: VoiceSuccessFeeSummaryLineItem[];
  total_success_fees_usd: number;
}

// ── Minutes ────────────────────────────────────────────────────────────────

export interface VoiceMinutesRemaining {
  success: boolean;
  message?: string;
  tier?: VoiceTier;
  monthly_included_minutes?: number;
  minutes_rolled_over?: number;
  total_available_minutes?: number;
  billing_period?: string;
}

// ── Usage Summary ──────────────────────────────────────────────────────────

export interface VoiceUsageSummary {
  success: boolean;
  message?: string;
  tenant_id?: number;
  billing_period?: string;
  tier?: VoiceTier;
  status?: VoiceSubscriptionStatus;
  total_sessions?: number;
  webrtc_sessions?: number;
  pstn_sessions?: number;
  total_minutes_used?: number;
  included_minutes?: number;
  overage_minutes?: number;
  overage_usd?: number;
  trial_sessions_used?: number;
  trial_sessions_remaining?: number;
  is_trial?: boolean;
}

// ── Billing action log / not-charged summary / trial activity ──────────────
// See docs/BACKEND_VOICE_BILLING_REQUIREMENTS.md — these endpoints don't exist
// in pricing-svc yet; service functions below fall back to placeholder data
// until they ship.

export interface VoiceBillingActionLogEntryApi {
  event_id: number;
  occurred_at: string;
  recipient_label: string;
  action_description: string;
  event_type: VoiceSuccessEventType;
  fee_usd: number;
}

export interface VoiceBillingActionLogResponse {
  success: boolean;
  tenant_id: number;
  billing_period: string;
  entries: VoiceBillingActionLogEntryApi[];
  total_count: number;
}

export interface VoiceNotChargedItemApi {
  label: string;
  detail: string;
  count?: number;
}

export interface VoiceNotChargedSummaryResponse {
  success: boolean;
  tenant_id: number;
  billing_period: string;
  items: VoiceNotChargedItemApi[];
}

export interface VoiceTrialActivityResponse {
  success: boolean;
  tenant_id: number;
  counts: Partial<Record<VoiceSuccessEventType, number>>;
}

// ── Tier display metadata (frontend-only) ─────────────────────────────────

export interface VoiceTierDisplay {
  key: VoiceTier;
  label: string;
  platformFeeCents: number;
  monthlyFee: number;
  includedMinutes: number;
  concurrentLimit: number;
  phoneNumbers: number;
  memoryTier: string;
  warmTransfer: boolean;
  urlPush: boolean;
  modelRouting: string;
  overageWebRTC: number;
  overagePSTN: number;
}

export const VOICE_TIER_DISPLAY: VoiceTierDisplay[] = [
  {
    key: 'voice_launch',
    label: 'Launch',
    platformFeeCents: 49900,
    monthlyFee: 499,
    includedMinutes: 2000,
    concurrentLimit: 50,
    phoneNumbers: 1,
    memoryTier: 'L2',
    warmTransfer: false,
    urlPush: false,
    modelRouting: 'fast',
    overageWebRTC: 0.45,
    overagePSTN: 0.60,
  },
  {
    key: 'voice_scale',
    label: 'Scale',
    platformFeeCents: 149900,
    monthlyFee: 1499,
    includedMinutes: 8000,
    concurrentLimit: 500,
    phoneNumbers: 3,
    memoryTier: 'L4',
    warmTransfer: true,
    urlPush: true,
    modelRouting: 'all',
    overageWebRTC: 0.35,
    overagePSTN: 0.48,
  },
  {
    key: 'voice_enterprise',
    label: 'Enterprise',
    platformFeeCents: 399900,
    monthlyFee: 3999,
    includedMinutes: 0,
    concurrentLimit: 1000,
    phoneNumbers: 10,
    memoryTier: 'L5',
    warmTransfer: true,
    urlPush: true,
    modelRouting: 'all',
    overageWebRTC: 0.25,
    overagePSTN: 0.35,
  },
];

export function getVoiceTierDisplay(tier: VoiceTier | null | undefined): VoiceTierDisplay | undefined {
  return VOICE_TIER_DISPLAY.find((t) => t.key === tier);
}

// ── Trial session severity (shared by the campaign overview and the sidebar panel) ──

export type VoiceTrialSeverity = 'info' | 'warning' | 'error';

export function getVoiceTrialSeverity(remaining: number, limit: number): VoiceTrialSeverity {
  if (limit <= 0) return 'info';
  if (remaining <= Math.max(2, Math.round(limit * 0.1))) return 'error';
  if (remaining <= Math.round(limit * 0.2)) return 'warning';
  return 'info';
}

/** True while the account is still on trial and free sessions are spent. */
export function isVoiceTrialExhausted(
  subscription: Pick<
    VoiceSubscription,
    'trial_active' | 'trial_sessions_remaining'
  > | null | undefined
): boolean {
  if (!subscription?.trial_active) return false;
  return subscription.trial_sessions_remaining <= 0;
}

// ── Requests ──────────────────────────────────────────────────────────────

export interface VoiceStartTrialRequest {
  tenant_id: number;
}

export interface VoiceChangeTierRequest {
  tenant_id: number;
  new_tier: VoiceTier;
  effective_immediately?: boolean;
  billing_cycle?: VoiceBillingCycle;
}

/**
 * First-time paid-plan selection (subscription.tier is still null) — distinct
 * from change-tier, which is for switching between two already-active tiers.
 * Requires a real Stripe subscription's IDs; see activateVoiceSubscription.
 */
export interface VoiceActivateRequest {
  tenant_id: number;
  tier: VoiceTier;
  stripe_customer_id: string;
  stripe_subscription_id: string;
  billing_period_start: string;
  billing_period_end: string;
}

export interface VoiceSetVerticalRequest {
  tenant_id: number;
  vertical: VoiceVertical | null;
  /** Only sent when vertical === 'other'. Not yet persisted server-side — see docs/BACKEND_VOICE_OUTCOME_BAND.md. */
  outcome_band?: VoiceOutcomeBand | null;
}

// ── Services / add-ons ──────────────────────────────────────────────────────

/** One catalog entry from GET /api/voice/services/{tenant_id}, annotated with this tenant's purchase history. */
export interface VoiceServiceCatalogItem {
  service_id: string;
  label: string;
  unit_price_usd: number;
  /** true = price × quantity; false = flat fee regardless of quantity. */
  per_unit: boolean;
  /** false = can only be purchased once — disable the purchase button once `purchased` is true. */
  repeatable: boolean;
  purchased: boolean;
  purchase_count: number;
}

export interface VoiceServiceCatalogResponse {
  success: boolean;
  message?: string;
  tenant_id: number;
  services: VoiceServiceCatalogItem[];
}

export interface VoiceServicePurchaseRequest {
  tenant_id: number;
  /** One of the `service_id` values from VoiceServiceCatalogItem, e.g. "activation", "compliance_review". */
  service_id: string;
  /** For per-unit services (per agent, per script set, per hour). Omit or 1 for flat one-time services. */
  quantity?: number;
}

export interface VoiceServicePurchaseResponse {
  success: boolean;
  message?: string;
  order_id?: string;
  invoice_url?: string;
}

export interface VoiceSetOutcomesTrackRequest {
  tenant_id: number;
  enabled: boolean;
}

// ── Alert preferences ────────────────────────────────────────────────────────

export interface VoiceAlertPreferenceApi {
  /** Matches AlertId in shared.tsx: 'minutes' | 'spend' | 'concurrent' | 'invoice' | 'payment'. */
  alert_id: string;
  enabled: boolean;
  /** e.g. '80%', '$1,000'. Null for alerts with no threshold (invoice, payment). */
  threshold: string | null;
}

export interface VoiceAlertPreferencesResponse {
  success: boolean;
  message?: string;
  alerts: VoiceAlertPreferenceApi[];
}

export interface VoiceAlertPreferencesRequest {
  tenant_id: number;
  alerts: VoiceAlertPreferenceApi[];
}

// ── Plan sizing ──────────────────────────────────────────────────────────────

/** GET /api/voice/sizing/{tenant_id} — returns defaults when not yet saved. */
export interface VoiceSizingResponse {
  audiencePerMonth: number;
  responseRatePct: number;
  bookPct: number;
  transferPct: number;
  isSaved: boolean;
}

/** PUT /api/voice/sizing/{tenant_id} */
export interface VoiceSizingRequest {
  audiencePerMonth: number;
  responseRatePct: number;
  bookPct: number;
  transferPct: number;
}
